// frontend/src/components/grid/productIdentity.js
// The single seam every merchant-facing item grid goes through for its identity
// column, so "show a Product cell on image-capable stores, else item_code +
// item_name" is defined once instead of hand-rolled per grid.
//
// WHY a seam and not just `productColumn`: the `store_type === 'shopify'` check
// and the product-vs-code column branch live here once, so no grid can wire
// the Product cell yet forget to plumb `image_url`/`sku` into its rows. Row
// height is not part of it: every grid has the same one (utils/agGridTheme.js
// ROW_HEIGHT).

import ProductCell from './ProductCell';
import { PRODUCT_FIELD_DEFAULTS } from './productFields';

// `useIsShopify` is defined in AuthContext (it is a store-capability predicate,
// not a grid concern) and re-exported here so grid authors get it alongside the
// column helpers; non-grid callers import it straight from AuthContext.
export { useIsShopify } from '../../contexts/AuthContext';

// colDef factory. The column's `field` is the grid's NAME field (default
// 'item_name'), which serves three purposes at once: it is the cell value
// (quick-filter, copy, export all see the product name), and — because AG Grid
// derives the colId from `field` — it is the sort key sent to the server.
// SSRM grids sort by colId through a strict alias filter that whitelists
// serializer wire names, so the sort key MUST be a real serializer field; a
// synthetic 'product' colId would 400. The renderer reads image/sku/code off
// params.data via the field mapping. Callers override the field names when
// their row shape uses different keys (e.g. the forecast grid's PascalCase).
//
// Sizing is the grid's decision, not this column's: a measured grid sizes it
// to the widest rendered cell, a fill grid spreads `flexColumn(2)` into the
// overrides. Only the
// default floor lives here; the name and SKU cut off with an ellipsis below it
// (ProductCell.css), so a grid with less room passes its own `minWidth`.
//
// `showImage: false` draws the cell without its thumbnail — a variant row
// under a product row that already carries the product's picture.
export function productColumn({
  headerName,
  imageField = PRODUCT_FIELD_DEFAULTS.imageField,
  nameField = PRODUCT_FIELD_DEFAULTS.nameField,
  skuField = PRODUCT_FIELD_DEFAULTS.skuField,
  codeField = PRODUCT_FIELD_DEFAULTS.codeField,
  showImage = true,
  ...colDefOverrides
} = {}) {
  return {
    field: nameField,
    headerName,
    minWidth: 240,
    cellRenderer: ProductCell,
    cellRendererParams: { imageField, nameField, skuField, codeField, showImage },
    ...colDefOverrides,
  };
}

// Empty SKU renders as an em-dash — the shared convention of the non-Shopify
// order-line grids that show a standalone SKU column.
const skuDashFormatter = (p) => p.value || '-';

// Identity column(s) for an item grid. On image-capable stores → the single
// merged Product cell (thumbnail + name + SKU). Otherwise → the raw
// item_code [+ sku] + item_name columns, where item_code is a value the
// merchant actually knows.
//
// Override slots keep this usable by every grid without re-deriving the branch:
//   - `product` / `code` / `sku` / `name`: colDef overrides spread onto that
//     column, so grids with PascalCase rows (forecast), nested `item.*` rows
//     (supplier items), computed titles, or their own sizing strategy (flex vs
//     autoSize) pass their field mapping / valueGetter / sizing here.
//   - `skuHeader` (or a `sku` override): emit the standalone SKU fallback column
//     between code and name — the order-line grids show it; reports don't, so it
//     is omitted unless asked for. Defaults to the em-dash formatter.
//   - `code: false` or `name: false`: omit that fallback column (a grid that
//     renders its own variant of it).
export function itemIdentityColumns(isShopify, {
  productHeader,
  product,
  codeHeader,
  code,
  skuHeader,
  sku,
  nameHeader,
  name,
} = {}) {
  if (isShopify) {
    return [productColumn({ headerName: productHeader, ...product })];
  }
  const cols = [];
  if (code !== false) cols.push({ field: 'item_code', headerName: codeHeader, ...code });
  if (skuHeader != null || sku != null) {
    cols.push({ field: 'sku', headerName: skuHeader, valueFormatter: skuDashFormatter, ...sku });
  }
  if (name !== false) cols.push({ field: 'item_name', headerName: nameHeader, minWidth: 150, ...name });
  return cols;
}
